import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import CreateRuleModal from '../components/kpi/CreateRuleModal';
import EditRuleModal from '../components/kpi/EditRuleModal';
import GenerateAIRulesModal from '../components/kpi/GenerateAIRulesModal';
import organisationKPIsService, { OrganisationKPIs } from '../services/organisationKPIsService';
import './OrganisationKPIRulesPage.css';

const OrganisationKPIRulesPage: React.FC = () => {
  const [existingRules, setExistingRules] = useState<OrganisationKPIs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<OrganisationKPIs | null>(null);

  useEffect(() => {
    fetchExistingRules();
  }, []);

  const fetchExistingRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const rules = await organisationKPIsService.getOrganisationKPIs();
      setExistingRules(rules);
    } catch (err) {
      console.error('Error fetching existing rules:', err);
      setError('Failed to load existing KPI rules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleRuleCreated = () => {
    fetchExistingRules();
  };

  const handleEditRule = (rule: OrganisationKPIs) => {
    setEditingRule(rule);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingRule(null);
  };

  const handleRuleUpdated = () => {
    fetchExistingRules();
  };

  const handleOpenAIModal = () => {
    setIsAIModalOpen(true);
  };

  const handleCloseAIModal = () => {
    setIsAIModalOpen(false);
  };

  const handleAIRulesGenerated = () => {
    fetchExistingRules();
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    try {
      await organisationKPIsService.deleteOrganisationKPI(ruleId);
      await fetchExistingRules();
      alert('Rule deleted successfully');
    } catch (err) {
      console.error('Error deleting rule:', err);
      alert('Failed to delete rule');
    }
  };



  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h1>Organisation KPI Rules</h1>
          <p className="dashboard-subtitle">
            Manage organisation-specific KPI validation rules and business logic
          </p>
        </div>

        <div className="content-container">
          <div className="org-kpi-rules-content">
            
            {/* Existing Rules Section */}
            <div className="rules-section">
              <div className="section-header">
                <h2>Existing KPI Rules</h2>
                <div className="header-actions">
                  <button 
                    className="ai-generate-button"
                    onClick={handleOpenAIModal}
                  >
                    🤖 Generate AI Rules
                  </button>
                  <button 
                    className="create-rule-button"
                    onClick={handleOpenModal}
                  >
                    Create New Rule
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading KPI rules...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                  <button onClick={fetchExistingRules} className="retry-button">
                    Retry
                  </button>
                </div>
              ) : existingRules.length > 0 ? (
                <div className="rules-list">
                  {existingRules.map(rule => (
                    <div key={rule.id} className="rule-card">
                      <div className="rule-header">
                        <div className="rule-info">
                          <h3 className="rule-id">{rule.ruleId}</h3>
                          <span className="rule-weight">Weight: {rule.ruleWeight}</span>
                        </div>
                        <div className="rule-actions">
                          <button 
                            className="edit-rule-button"
                            onClick={() => handleEditRule(rule)}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-rule-button"
                            onClick={() => handleDeleteRule(rule.id!)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <p className="rule-text">{rule.ruleText}</p>
                      <div className="rule-kpis">
                        <h4>Associated KPIs ({rule.kpis.length})</h4>
                        <div className="kpi-tags">
                          {rule.kpis.map((kpi, index) => (
                            <span key={index} className="kpi-tag">
                              {kpi.name} ({kpi.code})
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-rules">
                  <p>No KPI rules created yet</p>
                  <p className="empty-subtitle">Create your first rule to get started</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Create Rule Modal */}
      <CreateRuleModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onRuleCreated={handleRuleCreated}
      />

      {/* Edit Rule Modal */}
      <EditRuleModal 
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onRuleUpdated={handleRuleUpdated}
        rule={editingRule}
      />

      {/* Generate AI Rules Modal */}
      <GenerateAIRulesModal 
        isOpen={isAIModalOpen}
        onClose={handleCloseAIModal}
        onRulesGenerated={handleAIRulesGenerated}
        existingRules={existingRules}
      />
    </div>
  );
};

export default OrganisationKPIRulesPage;
